<template>
  <settings-page-card>
    <div class="flex items-center justify-between px-6">
      <h3 class="text-2xl font-semibold mb-0">
        Manage Roles
      </h3>

      <div class="flex items-center gap-x-3">
        <b-link
          v-if="checkPermissions('members.create')"
          variant="primary"
          class="inline-flex items-center justify-center !no-underline gap-[4px] h-[40px] px-[16px] rounded-lg border border-[#E2E5EC] hover:border-[#3967d7] bg-[#4F83E3] hover:bg-[#3967d7] text-white text-sm font-medium transition"
          to="roles/create"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="w-4 h-4"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>

          Create New Role
        </b-link>
      </div>
    </div>

    <div
      v-if="loading"
      class="flex flex-col items-center justify-center mt-12 w-full"
    >
      <b-spinner
        class="ml-2"
        variant="primary"
        size="xl"
      />
    </div>
    
    <div
      v-else
      class="flex flex-col mt-8 px-6"
    >
      <div
        v-if="checkPermissions('members.read')"
        class="border border-[#D7D7D7] rounded overflow-clip"
      >
        <table class="border-collapse table-auto w-full text-sm">
          <thead>
            <tr>
              <th
                v-for="(item, key) in tableHeaders"
                :key="key"
                class="bg-[#F9F9F9] border-b border-[#EFEFEF] font-semibold py-[10px] px-[20px] text-sm text-[#AAAAAA] text-left"
              >
                {{ item }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr
              v-for="(role) in roles"
              :key="role.id"
            >
              <td class="border-b border-[#EFEFEF] py-[10px] px-[20px] text-base text-[#45474A]">
                {{ role.name }}
              </td>
              <td class="border-b border-[#EFEFEF] py-[10px] px-[20px] text-base text-[#45474A] text-right">
                <div class="flex items-center gap-x-[10px] justify-end">
                  <b-link
                    v-if="checkPermissions('members.update')"
                    class="bg-transparent text-[#6B6767] hover:text-[#545150] p-1"
                    :to="`roles/${role.id}`"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-[18px] h-[18px]"
                    >
                      <path
                        d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z"
                        stroke="#6B6767"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.0455 11.0455C13.9547 11.2511 13.9276 11.4792 13.9677 11.7004C14.0078 11.9216 14.1133 12.1257 14.2705 12.2864L14.3114 12.3273C14.4382 12.4539 14.5387 12.6043 14.6074 12.7699C14.676 12.9354 14.7113 13.1128 14.7113 13.292C14.7113 13.4712 14.676 13.6487 14.6074 13.8142C14.5387 13.9798 14.4382 14.1302 14.3114 14.2568C14.1847 14.3836 14.0343 14.4842 13.8688 14.5528C13.7032 14.6214 13.5258 14.6568 13.3466 14.6568C13.1674 14.6568 12.9899 14.6214 12.8244 14.5528C12.6589 14.4842 12.5085 14.3836 12.3818 14.2568L12.3409 14.2159C12.1802 14.0587 11.9761 13.9533 11.7549 13.9132C11.5338 13.8731 11.3056 13.9001 11.1 13.9909C10.8983 14.0773 10.7264 14.2208 10.6052 14.4038C10.4841 14.5867 10.4191 14.8011 10.4182 15.0205V15.1364C10.4182 15.498 10.2745 15.8449 10.0188 16.1006C9.76305 16.3563 9.4162 16.5 9.05455 16.5C8.69289 16.5 8.34604 16.3563 8.09031 16.1006C7.83458 15.8449 7.69091 15.498 7.69091 15.1364V15.075C7.68563 14.8493 7.61258 14.6305 7.48126 14.4468C7.34993 14.2632 7.16641 14.1234 6.95455 14.0455C6.7489 13.9547 6.52078 13.9276 6.2996 13.9677C6.07842 14.0078 5.87433 14.1133 5.71364 14.2705L5.67273 14.3114C5.54608 14.4382 5.39569 14.5387 5.23015 14.6074C5.0646 14.676 4.88716 14.7113 4.70795 14.7113C4.52875 14.7113 4.35131 14.676 4.18576 14.6074C4.02022 14.5387 3.86983 14.4382 3.74318 14.3114C3.6164 14.1847 3.51582 14.0343 3.44719 13.8688C3.37857 13.7032 3.34325 13.5258 3.34325 13.3466C3.34325 13.1674 3.37857 12.9899 3.44719 12.8244C3.51582 12.6589 3.6164 12.5085 3.74318 12.3818L3.78409 12.3409C3.94128 12.1802 4.04672 11.9761 4.08682 11.7549C4.12693 11.5338 4.09985 11.3056 4.00909 11.1C3.92266 10.8983 3.77915 10.7264 3.59623 10.6052C3.4133 10.4841 3.19895 10.4191 2.97955 10.4182H2.86364C2.50198 10.4182 2.15513 10.2745 1.8994 10.0188C1.64367 9.76305 1.5 9.4162 1.5 9.05455C1.5 8.69289 1.64367 8.34604 1.8994 8.09031C2.15513 7.83458 2.50198 7.69091 2.86364 7.69091H2.925C3.15068 7.68563 3.36955 7.61258 3.55316 7.48126C3.73677 7.34993 3.87662 7.16641 3.95455 6.95455C4.04531 6.7489 4.07238 6.52078 4.03228 6.2996C3.99217 6.07842 3.88673 5.87433 3.72955 5.71364L3.68864 5.67273C3.56185 5.54608 3.46127 5.39569 3.39265 5.23015C3.32402 5.0646 3.2887 4.88716 3.2887 4.70795C3.2887 4.52875 3.32402 4.35131 3.39265 4.18576C3.46127 4.02022 3.56185 3.86983 3.68864 3.74318C3.81528 3.6164 3.96568 3.51582 4.13122 3.44719C4.29676 3.37857 4.47421 3.34325 4.65341 3.34325C4.83261 3.34325 5.01006 3.37857 5.1756 3.44719C5.34114 3.51582 5.49154 3.6164 5.61818 3.74318L5.65909 3.78409C5.81978 3.94128 6.02387 4.04672 6.24505 4.08682C6.46623 4.12693 6.69435 4.09985 6.9 4.00909H6.95455C7.15621 3.92266 7.32819 3.77915 7.44934 3.59623C7.57048 3.4133 7.63549 3.19895 7.63636 2.97955V2.86364C7.63636 2.50198 7.78003 2.15513 8.03576 1.8994C8.2915 1.64367 8.63834 1.5 9 1.5C9.36166 1.5 9.7085 1.64367 9.96424 1.8994C10.22 2.15513 10.3636 2.50198 10.3636 2.86364V2.925C10.3645 3.1444 10.4295 3.35876 10.5507 3.54168C10.6718 3.72461 10.8438 3.86812 11.0455 3.95455C11.2511 4.04531 11.4792 4.07238 11.7004 4.03228C11.9216 3.99217 12.1257 3.88673 12.2864 3.72955L12.3273 3.68864C12.4539 3.56185 12.6043 3.46127 12.7699 3.39265C12.9354 3.32402 13.1128 3.2887 13.292 3.2887C13.4712 3.2887 13.6487 3.32402 13.8142 3.39265C13.9798 3.46127 14.1302 3.56185 14.2568 3.68864C14.3836 3.81528 14.4842 3.96568 14.5528 4.13122C14.6214 4.29676 14.6568 4.47421 14.6568 4.65341C14.6568 4.83261 14.6214 5.01006 14.5528 5.1756C14.4842 5.34114 14.3836 5.49154 14.2568 5.61818L14.2159 5.65909C14.0587 5.81978 13.9533 6.02387 13.9132 6.24505C13.8731 6.46623 13.9001 6.69435 13.9909 6.9V6.95455C14.0773 7.15621 14.2208 7.32819 14.4038 7.44934C14.5867 7.57048 14.8011 7.63549 15.0205 7.63636H15.1364C15.498 7.63636 15.8449 7.78003 16.1006 8.03576C16.3563 8.2915 16.5 8.63834 16.5 9C16.5 9.36166 16.3563 9.7085 16.1006 9.96424C15.8449 10.22 15.498 10.3636 15.1364 10.3636H15.075C14.8556 10.3645 14.6412 10.4295 14.4583 10.5507C14.2754 10.6718 14.1319 10.8438 14.0455 11.0455Z"
                        stroke="#6B6767"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </b-link>

                  <delete-confirm
                    v-if="checkPermissions('members.delete')"
                    title="Are you sure you want to delete this role?"
                    description="The role will be deleted, the action cannot be undone."
                    confirm-variant="danger"
                    yes-btn-text="Yes"
                    no-btn-text="Cancel"
                    @confirm="handleDelete(role.id)"
                  >
                    <template #activator="{on}">
                      <b-btn
                        size="sm"
                        variant="transparent"
                        class="bg-transparent text-[#6B6767] hover:text-[#545150] p-1"
                        v-on="on"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="w-[18px] h-[18px]"
                        >
                          <path
                            d="M6.75 2.25H11.25M2.25 4.5H15.75M14.25 4.5L13.724 12.3895C13.6451 13.5732 13.6057 14.165 13.35 14.6138C13.1249 15.0088 12.7854 15.3265 12.3762 15.5248C11.9115 15.75 11.3183 15.75 10.132 15.75H7.86799C6.68168 15.75 6.08852 15.75 5.62375 15.5248C5.21457 15.3265 4.87507 15.0088 4.64999 14.6138C4.39433 14.165 4.35488 13.5732 4.27596 12.3895L3.75 4.5M7.5 7.875V11.625M10.5 7.875V11.625"
                            stroke="#6B6767"
                            stroke-width="1.2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </b-btn>
                    </template>
                  </delete-confirm>
                </div>
              </td>
            </tr>

            <tr v-if="!roles.length">
              <td
                colspan="4"
                class="text-center py-[14px] px-[20px] text-base text-[#AAAAAA]"
              >
                No roles found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </settings-page-card>
</template>

<script>
import SettingsPageCard from '@/layouts/dashboard/SettingsPageCard.vue'
import DeleteConfirm from '@/components/editor/modals/DeleteConfirm.vue'
import {mapActions, mapGetters, mapState} from 'vuex'

export default {
  name: 'ManageRoles',
  components: {DeleteConfirm, SettingsPageCard},
  data () {
    return {
      tableHeaders: ['Role', ''],
      roles: []
    }
  },
  computed: {
    ...mapState('roles', {
      loading: state => state.loading
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    })
  },
  methods: {
    ...mapActions({
      getRoles: 'roles/getRoles',
      deleteRole: 'roles/deleteRole'
    }),
    async handleDelete (id) {
      await this.deleteRole(id)
      this.roles = await this.getRoles(id)
    }
  },
  async mounted () {
    await this.$store.dispatch('projects/hasAccessToPage', 'members')
    this.roles = await this.getRoles()
  }
}
</script>